import { Fragment, useMemo } from 'react';
import { Divider } from '../../components/Divider';
import { Text } from '../../components/Text';
import { useSecuritiesContext } from '../../contexts';
import { useMarketsContext } from '../../contexts/MarketsContext';
import {
  MarketTypeEnum,
  WARNING_SEVERITY_TO_ALERT_VARIANT,
  type Market,
  type Order,
  type OrderMarketWarningItem,
  type Security,
} from '../../types';
import { withSoftBreaks } from '../../utils/string';
import { Alert } from '../Alert';
import { HStack, VStack } from '../Core';
import { IndicatorBadge, IndicatorBadgeSizes } from '../IndicatorBadge';
import { Table, TableSize, Tbody, Td, Tr } from '../Table';

type MarketGroup = MarketTypeEnum | 'Other';

interface OrderWarningsProps {
  warning?: Order['warning'];
  variant?: 'default' | 'mobileDetail';
}

function getMarketLabel(item: OrderMarketWarningItem, marketsByName: Map<string, Market>) {
  return marketsByName.get(item.market)?.DisplayName ?? item.market;
}

function getSymbolLabel(item: OrderMarketWarningItem, securitiesBySymbol: Map<string, Security>) {
  if (!item.symbol) {
    return undefined;
  }

  return securitiesBySymbol.get(item.symbol)?.DisplaySymbol ?? item.symbol;
}

function getCombinedItemLabel(
  item: OrderMarketWarningItem,
  marketsByName: Map<string, Market>,
  securitiesBySymbol: Map<string, Security>
) {
  let label = getMarketLabel(item, marketsByName);
  const symbolLabel = getSymbolLabel(item, securitiesBySymbol);
  if (symbolLabel != null) {
    label = `${label} (${symbolLabel})`;
  }

  return label;
}

export function OrderWarnings({ warning, variant = 'default' }: OrderWarningsProps) {
  const { marketsByName } = useMarketsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const groupCompareFn = useGroupCompareFn();
  const isMobileDetail = variant === 'mobileDetail';

  const warningGroups = useMemo(() => {
    if (warning == null) {
      return [];
    }
    const marketGroupsMap = new Map<string, OrderMarketWarningItem[]>();

    // Put warnings into groups (Exchange / Dealer).
    for (const item of warning.warningItems) {
      const marketType = marketsByName.get(item.market)?.Type ?? 'Other';
      const items = marketGroupsMap.get(marketType) ?? [];
      items.push(item);
      marketGroupsMap.set(marketType, items);
    }

    // Sort warnings.
    marketGroupsMap.forEach((arr, key) => {
      const sortedArr = arr.toSorted((a, b) =>
        getCombinedItemLabel(a, marketsByName, securitiesBySymbol).localeCompare(
          getCombinedItemLabel(b, marketsByName, securitiesBySymbol)
        )
      );
      marketGroupsMap.set(key, sortedArr);
    });

    const typeGroupTuples: [string, OrderMarketWarningItem[]][] = Array.from(marketGroupsMap.entries());

    // Sort groups by their type
    return typeGroupTuples.toSorted(([aType], [bType]) => groupCompareFn(aType, bType));
  }, [groupCompareFn, marketsByName, securitiesBySymbol, warning]);

  if (warning == null) {
    return null;
  }

  const alertVariant = WARNING_SEVERITY_TO_ALERT_VARIANT[warning.severity];

  const topLevelWarningsContent = (
    <VStack alignItems="flex-start" gap="spacing4" w="100%">
      {warning.topLevelWarnings.map((warningItem, idx) => (
        <HStack
          key={warningItem.label + warningItem.severity + idx}
          gap="spacing8"
          justifyContent="space-between"
          w="100%"
        >
          <Text size="fontSizeMd" color={isMobileDetail ? 'colorTextDefault' : 'gray.100'}>
            {warningItem.label}
          </Text>
          {warningItem.value && <IndicatorBadge noWrap size={IndicatorBadgeSizes.Small} children={warningItem.value} />}
        </HStack>
      ))}
    </VStack>
  );

  const table = warningGroups.length > 0 && (
    <Table w="100%" size={TableSize.Small}>
      <Tbody>
        {warningGroups.map(([type, items], idx) => (
          <Fragment key={type}>
            <Tr>
              <Td>
                <Text size="fontSizeSm" color="gray.080">
                  {`${type}s`}
                </Text>
              </Td>
            </Tr>
            {items.map(item => {
              const marketLabel = getMarketLabel(item, marketsByName);
              const symbolLabel = getSymbolLabel(item, securitiesBySymbol);

              return (
                <Tr key={`${item.market}-${item.symbol}`}>
                  <Td verticalAlign="top">
                    <HStack
                      gap="spacing4"
                      fontSize="fontSizeMd"
                      justifyContent="flex-start"
                      flexWrap="wrap"
                      pb={isMobileDetail ? 'spacing4' : undefined}
                      data-testid="order-market-warning-label"
                    >
                      <Text color="colorTextImportant">{marketLabel}</Text>
                      {symbolLabel && <Text color="colorTextSubtle">{symbolLabel}</Text>}
                    </HStack>
                  </Td>
                  <Td align="right" verticalAlign={isMobileDetail ? 'top' : undefined}>
                    <Text
                      size="fontSizeMd"
                      color="gray.090"
                      wordBreak="break-word"
                      overflowWrap="anywhere"
                      pb={isMobileDetail ? 'spacing4' : undefined}
                      data-testid="order-market-warning-value"
                    >
                      {withSoftBreaks(item.value)}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
            {warningGroups.length - 1 !== idx && (
              <Tr>
                <Td colSpan={2}>
                  <Divider my="spacing8" orientation="horizontal" />
                </Td>
              </Tr>
            )}
          </Fragment>
        ))}
      </Tbody>
    </Table>
  );

  return (
    <VStack
      gap="spacing16"
      w="100%"
      p={isMobileDetail ? undefined : 'spacing8'}
      maxWidth={isMobileDetail ? undefined : '70vw'}
    >
      <Alert
        w="100%"
        variant={alertVariant}
        dismissable={false}
        justifyContent={isMobileDetail ? undefined : 'space-between'}
        alignItems={isMobileDetail ? undefined : 'center'}
        fontSize={isMobileDetail ? 'fontSizeMd' : undefined}
        flatNegative={isMobileDetail}
      >
        {isMobileDetail ? (
          <VStack alignItems="flex-start" gap="spacing8" w="100%">
            {topLevelWarningsContent}
            {table}
          </VStack>
        ) : (
          topLevelWarningsContent
        )}
      </Alert>
      {!isMobileDetail && table}
    </VStack>
  );
}

const useGroupCompareFn = () => {
  const MARKET_GROUP_PRIORITY: Record<MarketGroup, number> = useMemo(
    () => ({
      [MarketTypeEnum.Exchange]: -10,
      [MarketTypeEnum.Dealer]: -9,
      [MarketTypeEnum.Custodian]: -8,
      [MarketTypeEnum.External]: -7,
      [MarketTypeEnum.InternalAlgo]: -6,
      [MarketTypeEnum.Dark]: -5,
      [MarketTypeEnum.Defi]: -4,
      Other: -3,
    }),
    []
  );

  return useMemo(
    () =>
      function groupCompareFn(a: string, b: string) {
        return (MARKET_GROUP_PRIORITY[a as MarketGroup] || 0) - (MARKET_GROUP_PRIORITY[b as MarketGroup] || 0);
      },
    [MARKET_GROUP_PRIORITY]
  );
};
